import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { EnvironmentOutlined, TeamOutlined, TrophyOutlined, GlobalOutlined } from "@ant-design/icons";
import "../styles/about-us.css";

const stats = [
  {
    icon: <EnvironmentOutlined />,
    label: "Located in",
    value: 40,
    suffix: "+",
    unit: "Countries",
  },
  {
    icon: <GlobalOutlined />,
    label: "Globally",
    value: 200,
    suffix: "+",
    unit: "Clients served",
  },
  {
    icon: <TeamOutlined />,
    label: "More than",
    value: 10000,
    suffix: "+",
    unit: "Global Workforce",
  },
  {
    icon: <TrophyOutlined />,
    label: "Received",
    value: 15,
    suffix: "+",
    unit: "Awards",
  },
];

function AnimatedCounter({ target, suffix = "", duration = 2000, delay = 0 }) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            
            setTimeout(() => {
              const startTime = Date.now();
              const startValue = 0;
              const endValue = target;

              const animate = () => {
                const now = Date.now();
                const elapsed = now - startTime;
                const progress = Math.min(elapsed / duration, 1);

                const easeOutQuart = 1 - Math.pow(1 - progress, 4);
                const currentValue = Math.floor(startValue + (endValue - startValue) * easeOutQuart);

                setCount(currentValue);

                if (progress < 1) {
                  requestAnimationFrame(animate);
                } else {
                  setCount(endValue);
                }
              };

              requestAnimationFrame(animate);
            }, delay);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => {
      if (counterRef.current) {
        observer.unobserve(counterRef.current);
      }
    };
  }, [target, duration, delay, hasAnimated]);

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  return (
    <span ref={counterRef} className="animated-counter">
      {formatNumber(count)}{suffix}
    </span>
  );
}

// Stat Card Component
function CircularStatCard({ stat, index }) {
  return (
    <div className="about-us-stat-card">
      <div className="about-us-stat-icon">{stat.icon}</div>
      <div className="about-us-stat-value">
        <AnimatedCounter 
          target={stat.value} 
          suffix={stat.suffix}
          duration={2000}
          delay={index * 150}
        />
      </div>
      <div className="about-us-stat-label">{stat.label}</div>
      <div className="about-us-stat-unit">{stat.unit}</div>
    </div>
  );
}

const values = [
  {
    title: "Creativity and Innovation",
    description: "We foster a culture of curiosity and innovation, encouraging novel ideas while embracing measured risks to exceed your expectations.",
    icon: "💡",
  },
  {
    title: "Flexibility",
    description: "We are committed to continually evolving our capabilities, allowing us to pivot swiftly in any situation and deliver enhanced value to our clients.",
    icon: "🔄",
  },
  {
    title: "Inclusivity",
    description: "As a certified minority business, we celebrate the diversity of culture, background, thought, and experience, recognizing that it enriches our team and enhances our solutions.",
    icon: "🤝",
  },
  {
    title: "Data-Driven Approach",
    description: "We rely on reliable and authentic data to drive deeper insights and inform focused decision-making.",
    icon: "📊",
  },
  {
    title: "Honesty and Integrity",
    description: "We prioritize honesty, fairness, and thoughtfulness in all our actions, always striving to do what is right for our customers.",
    icon: "🧩",
  },
  {
    title: "Clarity",
    description: "We build trust by ensuring that every interaction is grounded in honesty and authenticity.",
    icon: "📋",
  },
];

export default function AboutUs() {
  return (
    <div className="about-us-page" id="about-us">
      <section className="about-us-hero py-24 px-4">
        <div className="container">
          {/* <div className="about-us-breadcrumb mb-4">
            <Link to="/" className="about-us-breadcrumb-link">Home</Link>
            <span className="about-us-breadcrumb-separator"> &gt; </span>
            <span className="about-us-breadcrumb-current">About-Us</span>
          </div> */}

          <h1 className="about-us-title text-center mb-5">About Us</h1>

          <div className="about-us-content-wrapper mb-5">
            <div className="about-us-content">
              <p className="about-us-paragraph">
                Established in 2013 by a team of seasoned technology leaders, AmpsTek is rooted in extensive experience within the enterprise services sector. Based in New Jersey, our founders envisioned a company that prioritizes exceptional service, value, and scalability to support technology transformation and staffing needs.
              </p>
              <p className="about-us-paragraph">
                Since our inception, we have evolved into a global leader, dedicated to a comprehensive technology strategy that delivers cutting-edge, top-tier solutions. Our aim is to enhance customer engagement, empower businesses, and drive tangible results. With a diverse portfolio of over <span className="about-us-highlight">200 clients</span>, we successfully cater to the demands of millions of website visitors and thousands of users across desktop and mobile platforms.
              </p>
            </div>
          </div>

          <div className="about-us-stats-section">
            <div className="about-us-stats-grid">
              {stats.map((stat, index) => (
                <CircularStatCard key={index} stat={stat} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="about-us-values py-24 px-4">
        <div className="container">
          <h2 className="about-us-values-title text-center mb-5">Our Fundamental Values</h2>
          <div className="about-us-values-grid">
            {values.map((value, index) => (
              <div key={index} className="about-us-value-card">
                <div className="about-us-value-icon-wrapper">
                  <span className="about-us-value-icon">{value.icon}</span>
                </div>
                <h3 className="about-us-value-title">{value.title}</h3>
                <p className="about-us-value-description">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

